import React from 'react';
import {
  Box,
  Container,
  Divider,
  Grid,
  Hidden,
} from '@drivekyte/web-components';
import dynamic from 'next/dynamic';
import Image from 'next/image';

const WPTypography = dynamic(() => import('@/components/typography'));
const BulletList = dynamic(() => import('./components/bullet-list'));

import { FAQ_URL } from '@/config/global-constants';
import useTranslation from '@/hooks/use-translation';
import useAllFAQQuestions from './hooks/use-all-faq-questions';
import { Typography } from '@drivekyte/ui';

const Faq = () => {
  const { t } = useTranslation('components.growth.faq');
  const { getAllQuestions } = useAllFAQQuestions();
  const allQuestions = getAllQuestions();

  const openFaq = () => window.open(FAQ_URL, '_blank');

  return (
    <Container>
      <Box
        display="flex"
        flexDirection="column"
        marginBottom="xxxxLarge"
        marginTop="xxxxLarge"
      >
        <Grid container>
          <Box
            width="100%"
            display="flex"
            flexDirection="row"
            marginBottom="base"
            justifyContent="space-between"
          >
            <WPTypography
              variant="h2"
              color="primary"
              fontFamily="Gilroy"
              fontSize="28"
              lineHeight={32}
              fontWeight={700}
            >
              {t('title')}
            </WPTypography>
            <Hidden smDown>
              <Box
                display="flex"
                alignItems="center"
                cursor="pointer"
                onPress={openFaq}
              >
                <Box marginRight="tiny">
                  <Typography
                    variant="headingSmall"
                    color="$backgroundPositive"
                  >
                    {t('view-all-questions')}
                  </Typography>
                </Box>
                <Image
                  src="/images/icons/link.svg"
                  width={15}
                  height={15}
                  alt="view all questions"
                />
              </Box>
            </Hidden>
          </Box>
          <Hidden smDown>
            <Box width="100%" marginBottom="xxLarge">
              <Divider />
            </Box>
          </Hidden>
          <Grid container direction="row">
            {allQuestions.map(
              ({ title, answer, bulletPoints, observation }, index) => (
                <Grid
                  item
                  md={6}
                  xs={12}
                  justifyContent="center"
                  key={`question-${title}`}
                >
                  <Box
                    paddingVertical="base"
                    marginRight={index % 2 === 0 ? 'xLarge' : 'none'}
                  >
                    <Box marginBottom="tiny">
                      <Typography variant="labelLarge" color="primary">
                        {title}
                      </Typography>
                    </Box>
                    <Typography variant="paragraphMedium" color="quaternary">
                      {answer}
                    </Typography>
                    {!!bulletPoints && (
                      <Box marginLeft="base">
                        <BulletList bulletPoints={bulletPoints} />
                      </Box>
                    )}
                    {!!observation && (
                      <Typography variant="paragraphMedium" color="quaternary">
                        {observation}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              ),
            )}
            <Hidden smUp>
              <Grid>
                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  marginTop="base"
                  cursor="pointer"
                  onPress={openFaq}
                >
                  <Typography variant="labelMedium" color="$backgroundPositive">
                    {t('view-all-questions')}
                  </Typography>
                  <Box marginLeft="tiny">
                    <Image
                      src="/images/icons/link.svg"
                      width={20}
                      height={20}
                      alt="view all questions"
                    />
                  </Box>
                </Box>
              </Grid>
            </Hidden>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default Faq;
